import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import Navbar from "../components/Navbar";
// (BrainHealingLoop component removed from Landing — replaced with trust-signal stats card)
import ConditionsSection from "../components/ConditionsSection";
import PricingSection from "../components/PricingSection";
import SpecialistMatchQuiz from "../components/SpecialistMatchQuiz";
import BookOpinionForm from "../components/BookOpinionForm";
import HealthBlogsSection from "../components/HealthBlogsSection";
import CaseOutcomes from "../components/CaseOutcomes";
// (BrainHealingLoop video removed — replaced with trust-signal stats card)
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { Upload, Stethoscope, FileText, ShieldCheck, Clock, Users, ArrowRight, CheckCircle2, Award, Star, Youtube, Instagram, Facebook, Quote, ExternalLink, MessageCircle, Sparkles, PlayCircle, CreditCard, Search, BadgeIndianRupee, Heart, BadgeCheck, Globe } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1603039827000-30f55dd00ea7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBhYnN0cmFjdCUyMGJsdWUlMjBtZWRpY2FsJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzY2ODE1MjN8MA&ixlib=rb-4.1.0&q=85";
const DOC_IMG = "/images/hero_v2.png";
const MRI_IMG = "/images/specialists_hero.jpg";

// Assistance photos — "We're Here to Assist You at Every Step"
const ASSIST_STEPS = [
  { img: "/images/assist_upload.png", Icon: Upload,       title: "Case Upload Assistance", desc: "We help you upload reports and medical documents." },
  { img: "/images/assist_support.png", Icon: MessageCircle, title: "Support & Guidance",     desc: "Our team is available to guide you at every step." },
  { img: "/images/assist_secure.png",  Icon: ShieldCheck,   title: "Secure & Confidential",  desc: "Your data is safe with us and fully confidential." },
  { img: "/images/assist_care.png",    Icon: Heart,         title: "We Care For You",        desc: "Compassionate care, always by your side." },
];

export default function Landing() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [quizOpen, setQuizOpen] = useState(false);
  const heroPrice = useLocalePrice();
  const handleQuizFinish = ({ tag, priority, hasScan }) => {
    setQuizOpen(false);
    const q = new URLSearchParams({ as: "patient" });
    if (priority) q.set("priority", priority);
    if (tag && tag !== "other") q.set("tag", tag);
    if (hasScan) q.set("scan", hasScan);
    // After login, PatientDashboard reads these query params and pre-fills the wizard
    navigate(`/login?${q.toString()}`);
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-16 md:pb-0">
      <Navbar />

      {/* Referral welcome banner — appears when the URL carries ?ref=CODE */}
      <ReferralWelcomeBanner />

      {/* Mobile-only sticky booking bar — always visible at bottom while scrolling */}
      <StickyMobileBookBar heroPrice={heroPrice} />

      {/* Exit-intent recovery popup — desktop only, fires when cursor leaves top */}
      <ExitIntentPopup />

      {/* Hero — full-bleed background image of doctor holding MRI, text overlaid on left */}
      <section className="relative overflow-hidden border-b border-slate-200">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src={DOC_IMG}
            alt="Indian neurologist examining brain MRI scan"
            className="w-full h-full object-cover object-center"
            data-testid="hero-doctor-image"
          />
          {/* Gradient overlay — opaque on left for text legibility, fades over the doctor */}
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-white/70 lg:from-white lg:via-white/80 lg:to-white/30" aria-hidden />
          <div className="absolute inset-0 bg-gradient-to-t from-white/50 via-transparent to-transparent lg:hidden" aria-hidden />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center min-h-[560px] lg:min-h-[680px]">
          <div className="lg:col-span-7 xl:col-span-7">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-teal-50 border border-teal-100 text-teal-700 text-xs font-semibold tracking-wide shadow-sm" data-testid="hero-badge">
              <ShieldCheck className="w-3.5 h-3.5" /> India's Trusted Neuro Platform
            </div>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-[3.25rem] xl:text-6xl font-heading font-bold tracking-tight text-slate-900 leading-[1.1]">
              Expert Neuro &amp; Spine Second Opinion,<br/>
              <span className="text-teal-600">Backed by Experienced NIMHANS &amp; AIIMS Alumni</span><br/>
              <span className="text-slate-900">Top Neurosurgeons &amp; Neurologists</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-700 max-w-xl leading-relaxed">
              Get expert insights from top neurologists &amp; neurosurgeons and make informed decisions about your health.
            </p>

            {/* Two feature pills — Verified & Timely */}
            <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl" data-testid="hero-feature-cards">
              <div className="flex items-start gap-3 bg-white/80 backdrop-blur-sm rounded-xl p-3 border border-slate-200/70 shadow-sm">
                <div className="w-11 h-11 rounded-full bg-teal-50 border border-teal-100 grid place-items-center text-teal-600 shrink-0">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900 text-sm leading-tight">Verified Neurologists &amp; Neurosurgeons</div>
                  <div className="text-xs text-slate-500 mt-0.5 leading-tight">Experienced &amp; trusted specialists</div>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-white/80 backdrop-blur-sm rounded-xl p-3 border border-slate-200/70 shadow-sm">
                <div className="w-11 h-11 rounded-full bg-teal-50 border border-teal-100 grid place-items-center text-teal-600 shrink-0">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900 text-sm leading-tight">Timely Opinions</div>
                  <div className="text-xs text-slate-500 mt-0.5 leading-tight">Detailed reports within 24–48 hours</div>
                </div>
              </div>
            </div>

            {/* Dominant primary CTA — the entire funnel converges here */}
            <div className="mt-6 flex flex-wrap gap-3 items-center" data-testid="hero-cta-row">
              <a
                href="#booking-form"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-2 h-14 px-6 rounded-full bg-teal-600 hover:bg-teal-700 text-white text-base font-bold shadow-xl shadow-teal-600/30 hover:shadow-2xl hover:shadow-teal-600/40 transition-all active:scale-95"
              >
                <Upload className="w-5 h-5" />
                Get My Second Opinion
                <span className="ml-1 inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-white/25 border border-white/30 text-white text-xs font-semibold" data-testid="hero-cta-price">
                  from {heroPrice}
                </span>
                <ArrowRight className="w-4 h-4 opacity-0 -ml-1 group-hover:opacity-100 group-hover:ml-0 transition-all"/>
              </a>
              <a
                href="https://wa.me/918317674160?text=Hi%20NeuroOpinion%2C%20I%20need%20help%20booking%20a%20second%20opinion."
                target="_blank" rel="noreferrer"
                data-testid="hero-cta-whatsapp"
                className="inline-flex items-center gap-2 h-14 px-5 rounded-full bg-white border-2 border-green-500 text-green-700 hover:bg-green-50 text-sm font-semibold shadow-sm"
              >
                <MessageCircle className="w-5 h-5"/>
                <span>WhatsApp help</span>
                <span className="hidden sm:inline text-[11px] text-green-600 font-medium">· 24×7</span>
              </a>
            </div>

            {/* Secondary CTAs — kept for users who prefer to explore first */}
            <div className="mt-3 flex flex-wrap gap-4 items-center text-sm">
              <a href="#how-it-works" data-testid="hero-cta-how"
                 className="text-slate-600 hover:text-slate-900 font-medium inline-flex items-center gap-1">
                <PlayCircle className="w-4 h-4"/> How it works
              </a>
              <button
                type="button"
                onClick={() => setQuizOpen(true)}
                className="text-slate-600 hover:text-slate-900 font-medium inline-flex items-center gap-1"
                data-testid="hero-cta-quiz"
              >
                <Sparkles className="w-4 h-4"/> Find my specialist
              </button>
            </div>

            {/* Compact 3-step trust strip — reduces friction, right next to CTA */}
            <div
              className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-700"
              data-testid="hero-3steps"
            >
              <span className="inline-flex items-center gap-1.5"><span className="w-5 h-5 rounded-full bg-teal-600 text-white grid place-items-center text-[10px] font-bold">1</span>Upload MRI / CT / Reports</span>
              <span className="text-slate-300">›</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-5 h-5 rounded-full bg-teal-600 text-white grid place-items-center text-[10px] font-bold">2</span>Verified specialist reviews</span>
              <span className="text-slate-300">›</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-5 h-5 rounded-full bg-teal-600 text-white grid place-items-center text-[10px] font-bold">3</span>Written report in 24-48 hrs</span>
            </div>

            {/* Trust badge row — reassures on security, jurisdiction and credentials */}
            <div
              className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[11px] font-medium text-slate-600"
              data-testid="hero-trust-badges"
            >
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-emerald-600"/> SSL secured</span>
              <span className="inline-flex items-center gap-1.5"><span aria-hidden>🇮🇳</span> Data hosted in India</span>
              <span className="inline-flex items-center gap-1.5"><BadgeCheck className="w-3.5 h-3.5 text-teal-600"/> MCI / NMC verified</span>
              <span className="inline-flex items-center gap-1.5"><Heart className="w-3.5 h-3.5 text-rose-500"/> No card stored</span>
            </div>

            {/* Social proof row — patient avatars + count + star rating */}
            <SocialProofRow />
          </div>

          {/* Floating Book form on the right — sits over the doctor photo */}
          <div className="lg:col-span-5 xl:col-span-5 relative">
            <div className="lg:max-w-md lg:ml-auto" data-testid="hero-book-form-wrapper" id="booking-form">
              <BookOpinionForm source="landing-hero" />
            </div>
          </div>
        </div>
      </section>

      {/* We're Here to Assist You at Every Step — NEW */}
      <section id="how-it-works" className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">
              We're Here to Assist You at Every Step
            </h2>
            <div className="mt-3 inline-block w-16 h-1 rounded-full bg-teal-500" aria-hidden />
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {ASSIST_STEPS.map((s, i) => (
              <div
                key={s.title}
                className="rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm hover:shadow-md transition"
                data-testid={`assist-card-${i}`}
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
                  <img src={s.img} alt={s.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-5 flex items-start gap-3">
                  <div className="shrink-0 w-11 h-11 rounded-full bg-teal-500 grid place-items-center text-white shadow-sm">
                    <s.Icon className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-heading font-semibold text-base text-slate-900 leading-tight">{s.title}</div>
                    <div className="text-xs sm:text-sm text-slate-600 mt-1 leading-snug">{s.desc}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Conditions we treat */}
      <ConditionsSection />

      {/* For doctors */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-300">For Specialists</div>
            <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-semibold">Practice beyond your clinic.</h2>
            <p className="mt-4 text-slate-300 max-w-lg leading-relaxed">
              Help patients across rural India access your expertise. Set your own consultation fee. We handle payments, scheduling and secure scan delivery.
            </p>
            <ul className="mt-6 space-y-3 text-slate-200">
              <li className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-blue-300"/> Verified MCI/NMC onboarding</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-blue-300"/> Transparent payouts — your fee, on your schedule</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-blue-300"/> Asynchronous workflow — on your schedule</li>
            </ul>
            <div className="mt-8">
              <Link to="/signup">
                <Button size="lg" className="h-12 px-6 bg-white text-slate-900 hover:bg-slate-100" data-testid="doctor-onboard-cta">
                  {t("cta_join_doctor")} <ArrowRight className="ml-2 w-4 h-4"/>
                </Button>
              </Link>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="rounded-2xl overflow-hidden border border-slate-700">
              <img src={MRI_IMG} alt="MRI scans" className="w-full h-[360px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Verified Neurologists & Neurosurgeons */}
      <section className="bg-gradient-to-b from-white via-teal-50/20 to-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 border border-teal-100 text-teal-700 text-[11px] font-semibold tracking-wide">
              <BadgeCheck className="w-3.5 h-3.5" /> MCI / NMC verified
            </div>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
              Verified <span className="text-teal-600">Neurologists &amp; Neurosurgeons</span>
            </h2>
            <p className="mt-4 text-slate-600 text-sm sm:text-base leading-relaxed">
              Every specialist on our network is a NIMHANS, AIIMS or premier-institute alumnus with 10+ years of practice. Registration and credentials are verified before activation.
            </p>
          </div>
          <DoctorsPreview />
          <div className="mt-10 text-center">
            <Link to="/doctors">
              <Button size="lg" className="h-12 px-7 bg-slate-900 hover:bg-slate-800 text-white shadow-md rounded-full" data-testid="specialists-cta-patient">
                Browse all specialists <ArrowRight className="ml-2 w-4 h-4"/>
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials + social proof */}
      <Testimonials />

      {/* Real anonymised case outcomes (admin-curated) */}
      <CaseOutcomes />

      {/* Health Blogs — Max Healthcare style */}
      <HealthBlogsSection />

      {/* Pricing plans */}
      <PricingSection />

      {/* International Patients section — clear flow for UK/US/AE/Africa/etc */}
      <section id="international" className="bg-gradient-to-b from-white via-blue-50/30 to-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="max-w-2xl">
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[#0B5ED7] flex items-center gap-2">
              <Globe className="w-4 h-4"/> International Patients
            </div>
            <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-semibold text-slate-900 leading-tight">
              A specialist opinion from India — <span className="gradient-text">wherever you are.</span>
            </h2>
            <p className="mt-3 text-slate-600 text-sm sm:text-base leading-relaxed">
              Patients from the UK, USA, UAE, Africa, Australia and 100+ countries can access NIMHANS-trained
              neurosurgeons without travelling. Everything happens over WhatsApp + secure web checkout in USD.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl">
            {[
              { n: 1, t: "Select your country",   d: "Auto-detected from your location — you can change it anytime." },
              { n: 2, t: "Choose Regular or Priority", d: "Regular: 24-48 hr detailed report. Priority or Video Consultation: prioritised, <24 hrs." },
              { n: 3, t: "Upload MRI / CT / reports", d: "PDFs, DICOMs, images — encrypted end-to-end, up to 500 MB." },
              { n: 4, t: "Pay in USD via PayPal", d: "Africa: $50 Regular · $100 Priority or Video. Other countries: $100 Regular · $150 Priority or Video. Card or PayPal balance." },
              { n: 5, t: "Specialist assigned", d: "Board-certified Indian neurosurgeon reviews your case." },
              { n: 6, t: "Receive your written report", d: "Delivered via WhatsApp + email. Includes prescription if applicable." },
              { n: 7, t: "Video option — two paths", d: "Choose Video Consultation at booking (included in Priority tier) OR add a paid 20-min video follow-up after your Regular written report." },
            ].map((s) => (
              <div key={s.n} className="flex gap-4 p-5 rounded-xl bg-white border border-slate-200 hover:shadow-sm transition">
                <div className="w-9 h-9 rounded-full bg-[#0B5ED7] text-white grid place-items-center font-heading font-bold text-sm shrink-0">
                  {s.n}
                </div>
                <div>
                  <div className="font-heading font-semibold text-slate-900">{s.t}</div>
                  <div className="mt-0.5 text-sm text-slate-600">{s.d}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/login?as=patient">
              <Button className="h-11 bg-[#0B5ED7] hover:bg-[#084BB0] text-white shadow-md px-6" data-testid="intl-start-cta">
                Start my international consultation <ArrowRight className="w-4 h-4 ml-2"/>
              </Button>
            </Link>
            <a href="https://wa.me/918317674160?text=Hi%20NeuroOpinion%2C%20I%27m%20outside%20India%20and%20want%20a%20second%20opinion." target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-green-600 hover:bg-green-700 text-white text-sm font-semibold shadow-sm">
              💬 Chat first on WhatsApp
            </a>
          </div>

          <div className="mt-6 max-w-3xl text-[11px] text-slate-500 leading-relaxed">
            <b className="text-slate-700">Note:</b> NeuroOpinion offers specialist second opinions. Our
            <b> Priority or Video Consultation</b> tier means faster turnaround (&lt;24 hrs) — it is
            <b> not a substitute for emergency medical care</b>. If you or your loved one is facing a
            medical emergency, please visit your nearest emergency department immediately.
          </div>

          {/* Compact FAQ — 4 top questions international patients ask */}
          <div className="mt-12 max-w-4xl">
            <h3 className="font-heading text-2xl font-semibold text-slate-900">International FAQ</h3>
            <div className="mt-4 space-y-2" data-testid="intl-faq">
              {[
                {
                  q: "How long does the whole process take?",
                  a: "Regular: written report within 24-48 hours of payment. Priority or Video Consultation: less than 24 hours (video consult scheduled the same day when a slot is available).",
                },
                {
                  q: "Which currency will I be charged in?",
                  a: "India patients pay in INR — ₹1,000 Regular · ₹2,000 Priority or Video Consultation. Africa is charged $50 USD Regular · $100 USD Priority or Video. Other countries (UK, USA, UAE, Australia, Canada, EU, etc.) are charged $100 USD Regular · $150 USD Priority or Video. Amounts are fixed — no surprise conversion fees.",
                },
                {
                  q: "How do I pay from outside India?",
                  a: "PayPal Checkout — you can pay with any international Visa / Mastercard / American Express, or with a PayPal balance. No PayPal account required.",
                },
                {
                  q: "Can I do a video consultation with the doctor?",
                  a: "Yes — two ways. (1) At booking, pick Video Consultation under the Priority tier (₹2,000 / $100 / $150) — a 20-minute HD live call on Jitsi with your specialist instead of, or in addition to, the written report at no extra charge. (2) After a Regular written report is delivered, you can book an optional 20-minute video follow-up with the same specialist — a separate fee applies at the Priority-tier rate.",
                },
                {
                  q: "Is my data secure across borders?",
                  a: "All uploads are encrypted end-to-end and stored on Emergent's HIPAA-aligned object store in India. Reports are shared only with you and the assigned specialist.",
                },
              ].map((f, i) => (
                <details key={i} className="group rounded-xl border border-slate-200 bg-white open:shadow-sm">
                  <summary className="cursor-pointer flex items-center justify-between px-4 py-3.5 font-medium text-slate-800 text-sm select-none list-none">
                    <span>{f.q}</span>
                    <span className="text-slate-400 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                  </summary>
                  <div className="px-4 pb-4 text-sm text-slate-600 leading-relaxed">{f.a}</div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar — live counts from /api/analytics/public with a graceful
          static fallback if the endpoint is unreachable. */}
      <TrustBar />

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 py-8 text-sm text-slate-500 flex flex-wrap justify-between gap-4 items-center">
          <div className="flex flex-col gap-1">
            <div>© {new Date().getFullYear()} NeuroOpinion India</div>
            <a
              href="https://wa.me/918317674160"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-green-600 hover:text-green-700 font-medium"
              data-testid="footer-whatsapp"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp: +91 83176 74160
            </a>
            <a
              href="tel:+919441724072"
              className="inline-flex items-center gap-1.5 text-slate-600 hover:text-slate-900 font-medium text-xs"
              data-testid="footer-assistance"
            >
              📞 Assistance: +91 94417 24072
            </a>
          </div>
          <div className="flex gap-4 flex-wrap">
            <Link to="/conditions" className="hover:text-slate-900" data-testid="footer-conditions">Conditions</Link>
            <Link to="/blog" className="hover:text-slate-900" data-testid="footer-blog">Blog</Link>
            <Link to="/for-hospitals" className="hover:text-slate-900" data-testid="footer-hospital">For Hospitals</Link>
            <Link to="/verify-rx" className="hover:text-slate-900" data-testid="footer-verify-rx">Verify Rx</Link>
            <Link to="/contact" className="hover:text-slate-900" data-testid="footer-contact">Contact</Link>
            <Link to="/privacy" className="hover:text-slate-900" data-testid="footer-privacy">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-slate-900" data-testid="footer-terms">Terms of Service</Link>
            <Link to="/refund" className="hover:text-slate-900" data-testid="footer-refund">Refund Policy</Link>
            <Link to="/disclaimer" className="hover:text-slate-900" data-testid="footer-disclaimer">Medical Disclaimer</Link>
            <span className="flex items-center gap-1"><ShieldCheck className="w-4 h-4"/> HIPAA-aligned privacy</span>
          </div>
        </div>
      </footer>

      {/* Specialist match quiz modal — opens from hero CTA */}
      <SpecialistMatchQuiz
        open={quizOpen}
        onClose={() => setQuizOpen(false)}
        onFinish={handleQuizFinish}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// TrustBar — live counts on the Landing trust strip. Pulls `/analytics/public`
// so numbers reflect real DB state (verified doctors, completed opinions,
// published reviews) instead of vanity placeholders.
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// useLocalePrice — infer the visitor's currency from the browser locale +
// IANA timezone. Returns the hero-CTA fee string as a JSX-safe token so the
// button always shows the amount the visitor will actually pay at checkout.
// ---------------------------------------------------------------------------
function useLocalePrice() {
  const [price, setPrice] = useState("₹1,000");
  useEffect(() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
      const region = (Intl.DateTimeFormat().resolvedOptions().locale || "").split("-")[1]?.toUpperCase() || "";
      // Africa detection — coarse IANA prefix + a couple of common locales.
      const AFRICA_TZ_PREFIXES = ["Africa/"];
      const AFRICA_REGIONS = new Set(["NG","KE","ZA","GH","EG","TZ","UG","ET","MA","DZ","AO","SD","CM","CI","MZ","ZW"]);
      if (region === "IN" || tz.includes("Asia/Kolkata") || tz.includes("Asia/Calcutta")) {
        setPrice("₹1,000");
      } else if (AFRICA_TZ_PREFIXES.some(p => tz.startsWith(p)) || AFRICA_REGIONS.has(region)) {
        setPrice("$50");
      } else if (tz) {
        setPrice("$100");
      }
    } catch (_e) { /* keep INR default */ }
  }, []);
  return price;
}


// ---------------------------------------------------------------------------
// ExitIntentPopup — desktop-only recovery modal. Fires ONCE per session when
// the cursor moves toward the browser chrome (top edge). Hides for signed-in
// users and once dismissed. Uses sessionStorage flag so returning tabs don't
// re-trigger.
// ---------------------------------------------------------------------------
function ExitIntentPopup() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      if (JSON.parse(localStorage.getItem("user") || "null")) return;
      if (sessionStorage.getItem("noi_exit_shown") === "1") return;
    } catch (_e) { /* ignore */ }

    const onLeave = (e) => {
      // Fire only if cursor exits through the TOP of the viewport (browser
      // chrome / tab bar). Coming from side/bottom is normal window resize.
      if (e.clientY <= 0 && !e.relatedTarget) {
        setOpen(true);
        try { sessionStorage.setItem("noi_exit_shown", "1"); } catch (_e) { /* ignore */ }
        try { window.gtag?.("event", "exit_intent_shown"); } catch (_e) { /* gtag optional */ }
        document.removeEventListener("mouseout", onLeave);
      }
    };
    // Desktop only — mobile "exit intent" doesn't map to mouseleave.
    const isDesktop = window.matchMedia("(min-width: 768px) and (hover: hover)").matches;
    if (!isDesktop) return;
    // Wait 5s so we don't nag visitors who bounced on arrival.
    const t = setTimeout(() => document.addEventListener("mouseout", onLeave), 5000);
    return () => { clearTimeout(t); document.removeEventListener("mouseout", onLeave); };
  }, []);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center px-4 animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      data-testid="exit-intent-popup"
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
    >
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden">
        {/* Amber ribbon */}
        <div className="h-1.5 bg-gradient-to-r from-amber-400 via-orange-400 to-amber-400"/>
        <div className="p-6 sm:p-7">
          <div className="flex items-center gap-2 text-amber-700 text-xs font-bold uppercase tracking-widest">
            <Sparkles className="w-3.5 h-3.5"/> Wait — before you go
          </div>
          <h3 className="mt-2 font-heading text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">
            One quick <span className="text-emerald-600">second opinion</span> could change your care plan
          </h3>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">
            A verified NIMHANS / AIIMS-grade neurologist reviews your scans, gives you a written second opinion in <b>24-48 hours</b> — from ₹1,000. Takes 2 minutes to book.
          </p>
          <div className="mt-5 flex items-center gap-2">
            <a
              href="#booking-form"
              onClick={() => setOpen(false)}
              className="flex-1 inline-flex items-center justify-center gap-2 h-12 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-bold shadow-lg shadow-teal-600/30 transition-colors"
              data-testid="exit-intent-cta"
            >
              <Upload className="w-4 h-4"/> Book my opinion
            </a>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs text-slate-500 hover:text-slate-700 px-3 py-2"
              data-testid="exit-intent-dismiss"
            >
              No thanks
            </button>
          </div>
          <div className="mt-4 flex items-center gap-4 text-[10px] text-slate-400 uppercase tracking-widest">
            <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3"/> SSL secured</span>
            <span className="inline-flex items-center gap-1"><BadgeCheck className="w-3 h-3"/> MCI verified</span>
          </div>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// StickyMobileBookBar — full-width fixed bottom bar on mobile only. Always
// gives the visitor a 1-tap path to the primary CTA no matter how far they've
// scrolled. Auto-hides once the visitor is signed in (returning patient) so
// we don't nag them, and hides during active input focus so it doesn't cover
// the mobile keyboard.
// ---------------------------------------------------------------------------
function StickyMobileBookBar({ heroPrice = "₹1,000" }) {
  const [showBar, setShowBar] = useState(true);
  const [signedIn, setSignedIn] = useState(false);

  useEffect(() => {
    try {
      setSignedIn(!!JSON.parse(localStorage.getItem("user") || "null"));
    } catch (_e) { /* ignore */ }
    const onFocus = (e) => {
      if (e.target && /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName)) setShowBar(false);
    };
    const onBlur = () => setShowBar(true);
    document.addEventListener("focusin", onFocus);
    document.addEventListener("focusout", onBlur);
    return () => {
      document.removeEventListener("focusin", onFocus);
      document.removeEventListener("focusout", onBlur);
    };
  }, []);

  if (signedIn || !showBar) return null;
  return (
    <div
      className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200 shadow-[0_-4px_16px_rgba(0,0,0,0.06)] px-3 py-2.5 flex items-center gap-2"
      data-testid="mobile-sticky-cta"
    >
      <a
        href="#booking-form"
        className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-full bg-teal-600 text-white font-bold text-sm shadow-lg shadow-teal-600/30 active:scale-95 transition-transform"
        data-testid="mobile-sticky-primary"
      >
        <Upload className="w-4 h-4"/>
        Get Opinion · {heroPrice}
      </a>
      <a
        href="https://wa.me/918317674160?text=Hi%20NeuroOpinion%2C%20I%20need%20help%20booking%20a%20second%20opinion."
        target="_blank" rel="noreferrer"
        className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-green-600 text-white shadow-lg active:scale-95"
        data-testid="mobile-sticky-whatsapp"
        aria-label="WhatsApp help"
      >
        <MessageCircle className="w-5 h-5"/>
      </a>
    </div>
  );
}


function FirstTimeOfferBadge() {
  // Deprecated — the "first consult free" perk was removed on Feb 28, 2026.
  // Rendered as no-op to keep imports/mounts safe if any callers linger.
  return null;
}


function TrustBar() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/analytics/public").then(r => setStats(r.data)).catch(() => setStats(null));
  }, []);
  const doctorsN = stats?.verified_doctors ?? 10;
  const casesN = stats?.completed_cases ?? 0;
  const items = [
    { n: `${doctorsN}+`, l: "Verified specialists" },
    // Only show cases stat when we have real data — otherwise fall back
    // to the SLA promise which never changes.
    casesN > 0
      ? { n: `${casesN.toLocaleString()}+`, l: "Opinions delivered" }
      : { n: "24-48 hr", l: "Median turnaround" },
    { n: "₹1,000", l: "Written opinion" },
    { n: "100%", l: "Encrypted & private" },
  ];
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-2 md:grid-cols-4 gap-6" data-testid="trust-bar">
      {items.map((x) => (
        <div key={x.l} className="rounded-xl border border-slate-200 bg-white p-6">
          <div className="font-heading text-3xl font-bold text-slate-900">{x.n}</div>
          <div className="text-sm text-slate-500 mt-1">{x.l}</div>
        </div>
      ))}
    </section>
  );
}


// ---------------------------------------------------------------------------
// ReferralWelcomeBanner — shown at the very top of Landing when the URL
// carries `?ref=CODE`. Confirms the visitor is coming through a friend's
// share and previews the mutual ₹500 credit that will land after signup.
// ---------------------------------------------------------------------------


function ReferralWelcomeBanner() {
  const [refCode, setRefCode] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const code = (sp.get("ref") || "").trim().toUpperCase();
      if (code && code.length >= 4 && code.length <= 12) {
        setRefCode(code);
        // Fire GA4 landing event once — helpful for conversion tracking.
        try { window.gtag?.("event", "referral_landing_viewed", { code }); } catch (_e) { /* gtag optional */ }
      }
    } catch (_e) { /* SSR / privacy mode */ }
  }, []);

  if (!refCode || dismissed) return null;

  return (
    <div
      className="bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-500 text-white shadow-md"
      data-testid="referral-welcome-banner"
    >
      <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center gap-3 flex-wrap">
        <span className="text-lg" aria-hidden>🎁</span>
        <div className="flex-1 min-w-0 text-sm sm:text-[15px] leading-snug">
          <b>You&apos;re saving ₹500</b> with your friend&apos;s code{" "}
          <span className="font-mono font-bold px-1.5 py-0.5 rounded bg-white/25 border border-white/40">{refCode}</span>
          <span className="hidden sm:inline"> — sign up to auto-apply the credit on your first case.</span>
        </div>
        <Link
          to="/login?as=patient"
          className="text-xs sm:text-sm font-semibold px-3 py-1.5 rounded-full bg-white text-emerald-700 hover:bg-emerald-50 whitespace-nowrap"
          data-testid="referral-banner-cta"
        >
          Claim now →
        </Link>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          className="w-7 h-7 -mr-1 rounded-full text-white/80 hover:text-white hover:bg-white/15 grid place-items-center"
          data-testid="referral-banner-dismiss"
        >
          ✕
        </button>
      </div>
    </div>
  );
}



function SocialProofRow() {
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const [docs, setDocs] = useState([]);
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/doctors").then(r => setDocs(r.data.slice(0, 4))).catch(() => {});
    api.get("/analytics/public").then(r => setStats(r.data)).catch(() => setStats(null));
  }, []);
  const photo = (d) => d.photo_url
    ? (d.photo_url.startsWith("http") ? d.photo_url : `${BACKEND}${d.photo_url}`)
    : null;
  // Only render vanity numbers we can back with data. Falls back to
  // credential-based social proof if the analytics endpoint fails.
  const casesLabel = stats?.completed_cases > 0
    ? `${stats.completed_cases.toLocaleString()}+ opinions delivered`
    : "NIMHANS & AIIMS alumni";
  const reviewsLabel = stats?.published_reviews > 0
    ? `${stats.published_reviews}+ verified reviews`
    : "MCI / NMC verified";
  return (
    <div className="mt-7 flex items-center gap-5 flex-wrap" data-testid="hero-social-proof">
      {docs.length > 0 && (
        <div className="flex -space-x-2.5">
          {docs.map((d, i) => (
            photo(d) ? (
              <img
                key={d.id}
                src={photo(d)}
                alt={d.name}
                className="w-10 h-10 rounded-full object-cover ring-2 ring-white bg-slate-100 shadow-sm"
                style={{ zIndex: 10 - i }}
              />
            ) : (
              <div
                key={d.id}
                className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white grid place-items-center text-[11px] font-bold ring-2 ring-white shadow-sm"
                style={{ zIndex: 10 - i }}
              >
                {(d.name || "Dr").replace(/^Dr\.?\s*/i, "").slice(0, 2).toUpperCase()}
              </div>
            )
          ))}
        </div>
      )}
      <div className="flex items-center gap-5 text-sm">
        <div>
          <div className="font-bold text-slate-900 leading-tight" data-testid="hero-cases-stat">
            {stats?.completed_cases > 0 ? `${stats.completed_cases.toLocaleString()}+` : "10+"}
          </div>
          <div className="text-xs text-slate-500 leading-tight">{casesLabel}</div>
        </div>
        <div className="h-8 w-px bg-slate-200" />
        <div>
          <div className="flex items-center gap-1 text-amber-500 leading-none">
            {[0,1,2,3,4].map(i => <Star key={i} className="w-3.5 h-3.5 fill-amber-500"/>)}
            <span className="ml-1 text-slate-900 font-bold text-sm">4.9</span>
          </div>
          <div className="text-xs text-slate-500 mt-0.5 leading-tight" data-testid="hero-reviews-stat">
            {reviewsLabel}
          </div>
        </div>
      </div>
    </div>
  );
}

function DoctorsPreview() {
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const [docs, setDocs] = useState([]);
  useEffect(() => {
    api.get("/doctors").then(r => setDocs(r.data.slice(0, 6))).catch(() => {});
  }, []);
  if (docs.length === 0) return null;
  const photo = (d) => d.photo_url
    ? (d.photo_url.startsWith("http") ? d.photo_url : `${BACKEND}${d.photo_url}`)
    : null;
  return (
    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {docs.map(d => (
        <Link key={d.id} to={`/doctors/${d.id}`}>
          <Card className="relative p-6 border-slate-200 bg-white hover:shadow-lg hover:border-teal-300 hover:-translate-y-0.5 transition-all duration-200 h-full overflow-hidden rounded-2xl group" data-testid={`landing-doctor-${d.id}`}>
            {d.is_available !== false && (
              <span
                className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] font-semibold text-emerald-700"
                data-testid={`doctor-live-${d.id}`}
                title="Available for new cases today"
              >
                <span className="relative flex w-1.5 h-1.5">
                  <span className="absolute inset-0 rounded-full bg-emerald-500 opacity-75 animate-ping"/>
                  <span className="relative rounded-full w-1.5 h-1.5 bg-emerald-500"/>
                </span>
                Live
              </span>
            )}
            <div className="flex items-start gap-4">
              <div className="relative shrink-0">
                {photo(d) ? (
                  <img
                    src={photo(d)}
                    alt={d.name}
                    className="w-20 h-20 rounded-2xl object-cover object-top ring-2 ring-teal-100 bg-slate-100"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-teal-500 to-teal-700 text-white grid place-items-center">
                    <Stethoscope className="w-10 h-10"/>
                  </div>
                )}
                <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-emerald-500 ring-2 ring-white grid place-items-center" title="MCI/NMC verified">
                  <BadgeCheck className="w-3 h-3 text-white" />
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-heading font-bold text-slate-900 truncate flex items-center gap-1.5">
                  {d.name}
                  {/* Founder badge removed — `is_featured` used only for list ordering */}
                </div>
                <div className="text-xs text-slate-600 truncate">{d.specialization}</div>
                {d.alma_mater && (
                  <div className="text-[11px] text-teal-700 font-semibold mt-0.5 truncate">{d.alma_mater} alumnus</div>
                )}
                <div className="mt-2 flex items-center gap-3 text-xs text-slate-600 flex-wrap">
                  <span className="inline-flex items-center gap-1"><Award className="w-3.5 h-3.5 text-teal-600"/>{d.experience_years} yrs</span>
                  {d.rating > 0 && <span className="inline-flex items-center gap-1 text-amber-600 font-semibold"><Star className="w-3.5 h-3.5 fill-amber-500"/>{d.rating.toFixed(1)}</span>}
                  {d.median_response_hrs > 0 && (
                    <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold" title="Typical reply time — last 30 days">
                      <Clock className="w-3.5 h-3.5"/>~{d.median_response_hrs}h reply
                    </span>
                  )}
                  {typeof d.consultations_count === "number" && d.consultations_count > 0 && (
                    <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5"/>{d.consultations_count >= 1000
                      ? `${(d.consultations_count / 1000).toFixed(d.consultations_count >= 10000 ? 0 : 1)}K+`
                      : d.consultations_count.toLocaleString()}</span>
                  )}
                </div>
              </div>
            </div>
            {d.bio && <p className="mt-4 text-sm text-slate-600 line-clamp-3">{d.bio}</p>}
            <div className="mt-5 flex items-center justify-end pt-4 border-t border-slate-100">
              <span className="text-sm font-semibold text-teal-700 inline-flex items-center gap-1 group-hover:gap-2 transition-all">View profile <ArrowRight className="w-4 h-4"/></span>
            </div>
          </Card>
        </Link>
      ))}
    </div>
  );
}


// --------------------------------------------------------------------------
// Testimonials — pulled live from `/api/reviews/public` (verified completed
// cases only). Legacy `WRITTEN_TESTIMONIALS` fallback removed on Feb 26, 2026
// (founder decision — no illustrative placeholders).
// --------------------------------------------------------------------------
const VIDEO_TESTIMONIALS = [
  { platform: "youtube",   url: "", placeholder: true, label: "YouTube story", handle: "@NeuroOpinionIndia" },
  { platform: "instagram", url: "", placeholder: true, label: "Instagram reel", handle: "@neuroopinion.in" },
  { platform: "facebook",  url: "", placeholder: true, label: "Facebook video", handle: "NeuroOpinion India" },
];

// Replace this with your actual Google Business Profile URL once set up:
const GOOGLE_REVIEW_URL = "https://g.page/r/neuroopinion-india/review";

function Testimonials() {
  const [liveReviews, setLiveReviews] = React.useState([]);
  React.useEffect(() => {
    api.get("/reviews/public?limit=8&min_rating=4")
      .then((r) => setLiveReviews(r.data?.items || []))
      .catch(() => setLiveReviews([]));
  }, []);
  // Testimonials section is hidden entirely until we have ≥2 real, verified
  // patient reviews. No more illustrative placeholders — every quote users see
  // is from a real completed case. Founder decision — Feb 26, 2026.
  if (liveReviews.length < 2) return null;
  const items = liveReviews.slice(0, 4).map((r) => ({
    quote: r.text,
    name: r.patient_name_anon || "NeuroOpinion patient",
    city: r.doctor_name ? `Reviewed ${r.doctor_name}` : "Verified review",
    rating: r.rating,
  }));
  return (
    <section className="bg-gradient-to-b from-slate-50 to-white border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Header */}
        <div className="max-w-2xl">
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-700">Testimonials</div>
          <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-semibold text-slate-900">
            Real stories from real patients.
          </h2>
          <p className="mt-3 text-slate-600 text-sm sm:text-base">
            Every quote below is from a verified, completed second-opinion case — no invented stories.
          </p>
          <p className="mt-2 text-[11px] italic text-emerald-600" data-testid="testimonials-live-badge">
            ✓ Verified patient reviews · sourced from completed cases only
          </p>
        </div>

        {/* Written testimonial cards */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((t, i) => (
            <Card
              key={i}
              className="p-5 border-slate-200 bg-white flex flex-col gap-3 lift"
              data-testid={`testimonial-${i}`}
            >
              <Quote className="w-6 h-6 text-blue-600/70" />
              <p className="text-sm text-slate-700 leading-relaxed flex-1">"{t.quote}"</p>
              <div className="pt-3 border-t border-slate-100">
                <div className="flex items-center gap-0.5 mb-1 text-amber-500">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} className="w-3.5 h-3.5 fill-amber-500" />
                  ))}
                </div>
                <div className="font-heading font-semibold text-sm text-slate-900">{t.name}</div>
                <div className="text-xs text-slate-500">{t.city}</div>
              </div>
            </Card>
          ))}
        </div>

        {/* Video testimonials — YouTube / Instagram / Facebook */}
        <div className="mt-14">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-700">Watch on social</div>
              <h3 className="mt-2 font-heading text-2xl font-semibold text-slate-900">Video testimonials</h3>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
            {VIDEO_TESTIMONIALS.map((v, i) => (
              <VideoTestimonialCard key={i} v={v} index={i} />
            ))}
          </div>
        </div>

        {/* Google reviews CTA */}
        <div className="mt-14 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 flex items-center gap-5 flex-wrap">
          <div className="shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-yellow-400 via-red-400 to-blue-500 grid place-items-center text-white font-heading text-2xl font-bold shadow-sm">
            G
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="font-heading font-semibold text-lg text-slate-900">Loved your experience?</div>
            <div className="text-sm text-slate-600 mt-0.5">
              Share it on Google — your review helps other patients find us when they need a second opinion most.
            </div>
          </div>
          <a
            href={GOOGLE_REVIEW_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="google-review-cta"
          >
            <Button
              className="h-11 px-5 bg-blue-700 hover:bg-blue-800 text-white"
            >
              <Star className="w-4 h-4 mr-2 fill-white" /> Leave a Google review
              <ExternalLink className="w-3.5 h-3.5 ml-2 opacity-70" />
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
}

function VideoTestimonialCard({ v, index }) {
  const theme = {
    youtube: {
      Icon: Youtube,
      bg: "from-red-500 to-red-600",
      badge: "YouTube",
      badgeBg: "bg-red-50 text-red-700 border-red-200",
    },
    instagram: {
      Icon: Instagram,
      bg: "from-fuchsia-500 via-pink-500 to-amber-400",
      badge: "Instagram",
      badgeBg: "bg-pink-50 text-pink-700 border-pink-200",
    },
    facebook: {
      Icon: Facebook,
      bg: "from-blue-600 to-blue-700",
      badge: "Facebook",
      badgeBg: "bg-blue-50 text-blue-700 border-blue-200",
    },
  }[v.platform] || { Icon: Youtube, bg: "from-slate-600 to-slate-700", badge: v.platform, badgeBg: "bg-slate-50 text-slate-700 border-slate-200" };
  const { Icon } = theme;

  if (!v.placeholder && v.url) {
    // Real embed — for YouTube use iframe; for Instagram/Facebook use "Open post" link (their embeds require SDKs).
    const isYoutube = v.platform === "youtube";
    const ytId = isYoutube ? (v.url.match(/(?:v=|youtu\.be\/|\/embed\/)([A-Za-z0-9_-]{11})/)?.[1]) : null;
    return (
      <Card className="overflow-hidden border-slate-200 lift" data-testid={`video-testimonial-${index}`}>
        {isYoutube && ytId ? (
          <div className="aspect-video w-full bg-black">
            <iframe
              src={`https://www.youtube.com/embed/${ytId}`}
              title={v.label}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        ) : (
          <a href={v.url} target="_blank" rel="noreferrer" className={`block aspect-video w-full bg-gradient-to-br ${theme.bg} grid place-items-center text-white`}>
            <Icon className="w-12 h-12" />
          </a>
        )}
        <div className="p-4 flex items-center justify-between gap-2">
          <div>
            <div className={`inline-block text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${theme.badgeBg}`}>{theme.badge}</div>
            <div className="mt-1.5 text-sm font-semibold text-slate-900">{v.label}</div>
            <div className="text-xs text-slate-500">{v.handle}</div>
          </div>
          <a href={v.url} target="_blank" rel="noreferrer" className="text-blue-700 text-sm font-semibold inline-flex items-center gap-1">
            Watch <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </Card>
    );
  }

  // Placeholder — coming soon tile
  return (
    <Card
      className="overflow-hidden border-slate-200 lift"
      data-testid={`video-testimonial-placeholder-${index}`}
    >
      <div className={`aspect-video w-full bg-gradient-to-br ${theme.bg} grid place-items-center relative`}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.15),transparent_60%)]" />
        <Icon className="w-14 h-14 text-white/90 relative z-10" />
      </div>
      <div className="p-4 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className={`inline-block text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${theme.badgeBg}`}>{theme.badge}</div>
          <div className="mt-1.5 text-sm font-semibold text-slate-900">{v.label}</div>
          <div className="text-xs text-slate-500">Coming soon</div>
        </div>
        <span className="text-xs text-slate-400 italic">Stay tuned</span>
      </div>
    </Card>
  );
}
